import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useEmployeeApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams, formatDate, formatDateTime } from '../../utils/Utils';
import { exitReasonLabel } from './exitInfo';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { getEmployeeMfaStatus, resetEmployeeMfa } from '../../services/mfaService';
import { getEmployeeLoginLock, unlockEmployeeLogin } from '../../services/loginLockService';

const Container = styled.div``;

const StatusToggle = styled.div`
  display: inline-flex;
  gap: 4px;
  background: #EEF2F6;
  border-radius: 8px;
  padding: 4px;
  margin-bottom: 16px;
`;

const StatusButton = styled.button`
  border: none;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary.main : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : '#5B6573')};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 13px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease;
`;

const InfoNote = styled.div`
  background: #fff7ed; border: 1px solid #fed7aa; color: #9a3412;
  border-radius: 6px; padding: 10px 12px; font-size: 13px; margin: 0; line-height: 1.4;
`;

export default function EmployeesPage() {
  const { t, i18n } = useTranslation();
  const { canWrite, hasModule } = useAuth();
  const history = useHistory();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [terminateTarget, setTerminateTarget] = useState(null);
  // Hatalı kayıt temizliği: onay çekmecesi ve — engellenirse — bulunan izlerin dökümü.
  const [deleteRecordTarget, setDeleteRecordTarget] = useState(null);
  // İki adımlı doğrulama sıfırlama: { id, name, status } — status yüklenene kadar null.
  const [mfaResetTarget, setMfaResetTarget] = useState(null);
  // AKM POC §9.2 — giriş kilidini açma: { id, name, status } — status yüklenene kadar null.
  const [unlockTarget, setUnlockTarget] = useState(null);
  const [blockedTraces, setBlockedTraces] = useState(null);
  // Active employees by default; "former" shows soft-deleted ones for re-hire.
  const [status, setStatus] = useState('active');
  const [reactivateRow, setReactivateRow] = useState(null);
  const isFormer = status === 'former';

  const effectiveParams = useMemo(() => ({ ...queryParams, status }), [queryParams, status]);

  const {
    objectSchema, fetching, columns: baseColumns, data, pagination,
    create, update, terminate, deleteRecord, reactivate, odActive, exitReasonOptions,
  } = useEmployeeApi(effectiveParams);

  // Yeni kayıt
  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  // Düzenleme
  const handleEdit = useCallback((row) => {
    setSelectedRow({
      ...row,
      dateOfBirth: row.dateOfBirth ? row.dateOfBirth.substring(0, 10) : '',
      employmentDate: row.employmentDate ? row.employmentDate.substring(0, 10) : '',
      // HR Analytics yaşam döngüsü alanları: backend DateTime? döndürür (tam ISO),
      // dateOfBirth/employmentDate ile aynı nedenle (batı saat dilimlerinde gün
      // kayması) yyyy-MM-dd'ye kısaltılır.
      contractEndDate: row.contractEndDate ? row.contractEndDate.substring(0, 10) : '',
      probationEndDate: row.probationEndDate ? row.probationEndDate.substring(0, 10) : '',
      terminationDate: row.terminationDate ? row.terminationDate.substring(0, 10) : '',
    });
    setDrawerOpen(true);
  }, []);

  // Kaydetme
  const handleSave = useCallback(async (formData) => {
    const success = selectedRow
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  // Tekli çıkarma
  const handleTerminate = useCallback((row) => {
    setTerminateTarget({ ids: [row.id], name: `${row.firstName} ${row.lastName}` });
  }, []);

  // Toplu çıkarma — aynı form, tek tarih + tek neden.
  const handleRowsTerminate = useCallback((ids) => {
    setTerminateTarget({ ids, name: '' });
  }, []);

  const handleTerminateSave = useCallback(async (formData) => {
    if (!terminateTarget) return false;
    const success = await terminate({
      ids: terminateTarget.ids,
      terminationDate: formData.terminationDate || null,
      exitReasonCode: formData.exitReasonCode,
    });
    if (success) setTerminateTarget(null);
    return success;
  }, [terminateTarget, terminate]);

  // Hatalı kayıt — mükerrer import satırı, yanlış kişi, deneme kaydı. İşten
  // çıkarmadan AYRI: çıkarma bir istihdamı sonlandırır, bu ise hiç var olmamış bir
  // istihdamı kayıttan düşürür. Bu yüzden OD aktif/pasif FARK ETMEZ.
  const handleDeleteRecord = useCallback((row) => {
    setDeleteRecordTarget({ id: row.id, name: `${row.firstName} ${row.lastName}` });
  }, []);

  const handleResetMfa = useCallback((row) => {
    const name = [row?.firstName, row?.lastName].filter(Boolean).join(' ') || row?.name || '';
    setMfaResetTarget({ id: row.id, name, status: null });
    getEmployeeMfaStatus(row.id).then((res) => {
      const status = res?.data?.data;
      setMfaResetTarget((prev) => (prev && prev.id === row.id ? { ...prev, status: status || { enabled: false } } : prev));
    });
  }, []);

  const handleUnlockLogin = useCallback((row) => {
    const name = [row?.firstName, row?.lastName].filter(Boolean).join(' ') || row?.name || '';
    setUnlockTarget({ id: row.id, name, status: null });
    getEmployeeLoginLock(row.id).then((res) => {
      const status = res?.data?.status === false ? { locked: false } : res?.data?.data;
      setUnlockTarget((prev) => (prev && prev.id === row.id ? { ...prev, status: status || { locked: false } } : prev));
    });
  }, []);

  // Kilitli değilse kaydet false döner — çekmece açık kalır (yapılacak bir şey yok).
  const handleUnlockSave = useCallback(async () => {
    if (!unlockTarget?.status?.locked) return false;
    await unlockEmployeeLogin(unlockTarget.id);
    return true;
  }, [unlockTarget]);

  const handleResetMfaSave = useCallback(async () => {
    if (!mfaResetTarget?.status?.enabled) return false;
    await resetEmployeeMfa(mfaResetTarget.id);
    return true;
  }, [mfaResetTarget]);

  const handleDeleteRecordSave = useCallback(async () => {
    if (!deleteRecordTarget) return false;
    const { ok, traces } = await deleteRecord(deleteRecordTarget.id);
    if (ok) {
      setDeleteRecordTarget(null);
      return true;
    }
    // İz bulundu: çekmeceyi kapat, NE bulunduğunu dökümüyle göster. Kuru bir
    // "silinemez" kullanıcıya ne yapacağını söylemez.
    if (traces && Object.keys(traces).length > 0) {
      setDeleteRecordTarget(null);
      setBlockedTraces({ name: deleteRecordTarget.name, traces });
      return true;
    }
    return false;
  }, [deleteRecordTarget, deleteRecord]);

  // OD AKTİF kiracıda çıkış ayrılış sürecinin işidir; liste yalnız YÖNLENDİRİR.
  // Süreci olan çalışanda doğrudan o sürecin detayına, olmayanda süreç açma
  // çekmecesini önden doldurulmuş halde açan ayrılış ekranına gider.
  const handleOpenOffboarding = useCallback((row) => {
    if (row.activeOffboardingId) {
      history.push(`/offboarding/${row.activeOffboardingId}`);
      return;
    }
    history.push('/offboarding', {
      startForEmployeeId: row.id,
      startForEmployeeName: `${row.firstName} ${row.lastName}`.trim(),
    });
  }, [history]);

  // Durum geçişi (Aktif / Çıkmış) — sayfalamayı sıfırla
  const handleStatusChange = useCallback((next) => {
    if (next === status) return;
    setStatus(next);
    setFilterList([]);
    setQueryParams({ pageSize: 10, pageNumber: 1 });
  }, [status]);

  // Tekrar işe alma akışı
  const reactivateSchema = useMemo(() => ([
    { name: 'employmentDate', label: t('employee.rehireDate'), type: 'date', grid: 12, required: true },
    // Çıkan çalışanın kurumsal adresi serbest kalır ve başkasına verilmiş olabilir; bu alan
    // İK'nın geri alımda yeni adresi tek adımda vermesini sağlar. Boş bırakılırsa eski adres
    // korunur — başkasındaysa backend 409 ile uyarır.
    {
      name: 'corporateEmail', label: t('employee.corporateEmail'), type: 'email', grid: 12,
      description: t('employee.rehireEmailHint'),
    },
  ]), [t]);

  const handleReactivate = useCallback((row) => {
    setReactivateRow(row);
  }, []);

  const handleReactivateSave = useCallback(async (formData) => {
    if (!reactivateRow) return false;
    const success = await reactivate(reactivateRow.id, formData.employmentDate, formData.corporateEmail);
    if (success) setReactivateRow(null);
    return success;
  }, [reactivateRow, reactivate]);

  // Tablo değişikliği
  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  // Filtre onay
  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  // Satır tıklama
  const handleRowClick = useCallback((row) => {
    history.push(`/employees/${row.id}`, { user: row });
  }, [history]);

  // Aksiyon menüsü — çıkmış personellerde yalnızca "Tekrar İşe Al"
  // (detay sayfası pasif çalışanları açmaz).
  const actionOptions = useMemo(() => {
    if (isFormer) {
      return canWrite('EM')
        ? [{ type: 'edit', label: t('employee.rehire'), onClick: handleReactivate }]
        : [];
    }
    const options = [
      { type: 'details', label: t('employee.detail'), onClick: (row) => history.push(`/employees/${row.id}`, { user: row }) },
    ];
    if (canWrite('EM')) {
      options.push({ type: 'edit', label: t('employee.edit'), onClick: handleEdit }, { type: 'divider' });
      // OD AKTİFSE "İşten Çıkar" HİÇ GÖSTERİLMEZ. Kozmetik bir gizleme değil:
      // backend de 409 `employee.exitManagedByOffboarding` döndürüyor. Yanlış yolu
      // gösterip tıklayınca hata vermek yerine, doğru yolu tek aksiyon olarak
      // bırakıyoruz — etiket, sürecin durumuna göre değişir.
      options.push(odActive
        ? {
          // 'open' — 'details' DEĞİL: OptionsMenu action.type'ı React key olarak
          // kullanıyor, aynı menüde iki 'details' mükerrer key üretirdi.
          type: 'open',
          label: (row) => (row?.activeOffboardingId
            ? t('employee.openOffboarding')
            : t('employee.startOffboarding')),
          onClick: handleOpenOffboarding,
        }
        : { type: 'delete', label: t('employee.terminate'), onClick: handleTerminate });

      // Hatalı kayıt temizliği HER İKİ kiracı tipinde de var: bu bir çıkış değil,
      // dolayısıyla ayrılış süreci sözleşmesine girmiyor. OD'li kiracıda hatalı
      // kaydı temizlemenin başka yolu da kalmamıştı.
      options.push({ type: 'archive', label: t('employee.deleteRecord'), onClick: handleDeleteRecord });
      options.push({ type: 'reset_mfa', label: t('mfa.admin.resetAction'), onClick: handleResetMfa });
      options.push({ type: 'unlock_login', label: t('loginLock.unlockAction'), onClick: handleUnlockLogin });
    }
    return options;
  }, [t, canWrite, handleEdit, handleTerminate, handleDeleteRecord, handleReactivate, handleOpenOffboarding, handleResetMfa, handleUnlockLogin, history, isFormer, odActive]);

  // Giriş kilidi çekmecesi: alan yok, durum + ne olacağını anlatan blok.
  const unlockSchema = useMemo(() => {
    const status = unlockTarget?.status;
    let content;
    if (!status) content = t('common.loading');
    else if (!status.locked) content = t('loginLock.notLockedInfo', { name: unlockTarget?.name || '' });
    else {
      content = t('loginLock.unlockIntro', {
        name: unlockTarget?.name || '',
        until: status.lockedUntil ? formatDateTime(status.lockedUntil, i18n.language) : '-',
      });
    }
    return [{ name: '_notice', type: 'notice', grid: 12, tone: status?.locked ? 'warning' : undefined, content }];
  }, [t, i18n.language, unlockTarget]);

  // MFA sıfırlama çekmecesi: alan yok, durum + ne olacağını anlatan blok.
  const mfaResetSchema = useMemo(() => {
    const status = mfaResetTarget?.status;
    let content;
    if (!status) {
      content = t('common.loading');
    } else if (!status.enabled) {
      content = t('mfa.admin.notEnabled', { name: mfaResetTarget?.name || '' });
    } else {
      content = (
        <>
          {t('mfa.admin.resetIntro', {
            name: mfaResetTarget?.name || '',
            date: formatDate(status.enabledAt, i18n.language),
          })}
          <ul>
            <li>{t('mfa.admin.resetEffectCodes')}</li>
            <li>{t('mfa.admin.resetEffectNextLogin')}</li>
            <li>{t('mfa.admin.resetEffectEmail')}</li>
          </ul>
        </>
      );
    }
    return [{ name: '_notice', type: 'notice', grid: 12, tone: status?.enabled ? 'warning' : undefined, content }];
  }, [t, i18n.language, mfaResetTarget]);

  // Onay çekmecesi: alan yok, yalnız ne olduğunu anlatan blok. "Sil" kelimesi
  // yıllarca işten çıkarma anlamında kullanıldığı için farkı açıkça yazıyoruz.
  const deleteRecordSchema = useMemo(() => ([
    {
      name: '_notice', type: 'notice', grid: 12, tone: 'warning',
      content: (
        <>
          {t('employee.deleteRecordNoticeIntro', { name: deleteRecordTarget?.name || '' })}
          <ul>
            <li>{t('employee.deleteRecordNoticeWhen')}</li>
            <li>{t('employee.deleteRecordNoticeNotExit')}</li>
            <li>{t('employee.deleteRecordNoticeGate')}</li>
          </ul>
        </>
      ),
    },
  ]), [t, deleteRecordTarget]);

  // Çıkarma formu. Üstteki `notice` işlemin ne yaptığını sayar — "Sil" etiketi
  // yıllarca kalıcı silme çağrıştırdı, oysa kayıt durur.
  const terminateSchema = useMemo(() => ([
    {
      name: '_notice', type: 'notice', grid: 12,
      content: (
        <>
          {terminateTarget && terminateTarget.ids.length > 1
            ? t('employee.terminateNoticeBulkIntro', { count: terminateTarget.ids.length })
            : t('employee.terminateNoticeIntro', { name: terminateTarget?.name || '' })}
          <ul>
            <li>{t('employee.terminateNoticeAccess')}</li>
            <li>{t('employee.terminateNoticeAssignments')}</li>
            <li>{t('employee.terminateNoticeList')}</li>
            <li>{t('employee.terminateNoticeRehire')}</li>
          </ul>
        </>
      ),
    },
    {
      name: 'terminationDate', label: t('employee.lastWorkDay'), type: 'date', grid: 6, required: true,
      maxDate: new Date().toISOString().slice(0, 10),
      description: t('employee.lastWorkDayHint'),
    },
    {
      name: 'exitReasonCode', label: t('employee.exitReasonCode'), type: 'select', grid: 6, required: true,
      options: exitReasonOptions, idKey: 'id', labelKey: 'name',
    },
  ]), [t, exitReasonOptions, terminateTarget]);

  // Çıkmış Personeller sekmesinde işten çıkış tarihi + nedeni görünür (müşteri
  // geri bildirimi 2026-08). Elle deaktive edilenlerde kaynak veri yoktur → "-".
  // Görüntüleme-amaçlı: BE sıralama/filtre desteği bu kolonlar için yok.
  const exitColumns = useMemo(() => (isFormer ? [
    {
      name: 'terminationDate',
      label: t('employee.terminationDate'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (v ? formatDate(v, i18n.language) : '-'),
      },
    },
    {
      name: 'exitReasonCode',
      label: t('employee.exitReasonCode'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => exitReasonLabel(t, v),
      },
    },
  ] : []), [isFormer, t, i18n.language]);

  // Aksiyonlu kolonlar
  const columns = useMemo(() => {
    if (actionOptions.length === 0) return [...baseColumns, ...exitColumns];
    return [
      ...baseColumns,
      ...exitColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, exitColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('employee.edit') : t('employee.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      >
        {hasModule('OD') && <InfoNote>{t('employee.exitFieldsManagedByOffboarding')}</InfoNote>}
      </GenericFormDrawer>

      <GenericFormDrawer
        open={!!reactivateRow}
        onClose={() => setReactivateRow(null)}
        title={t('employee.rehire')}
        schema={reactivateSchema}
        initialValues={reactivateRow ? { employmentDate: '' } : null}
        onSave={handleReactivateSave}
      />

      <GenericFormDrawer
        open={!!terminateTarget}
        onClose={() => setTerminateTarget(null)}
        title={terminateTarget && terminateTarget.ids.length > 1
          ? t('employee.bulkTerminateTitle', { count: terminateTarget.ids.length })
          : t('employee.terminateTitle', { name: terminateTarget?.name || '' })}
        schema={terminateSchema}
        initialValues={terminateTarget
          ? { terminationDate: new Date().toISOString().slice(0, 10), exitReasonCode: '' }
          : null}
        onSave={handleTerminateSave}
      />

      <GenericFormDrawer
        open={!!deleteRecordTarget}
        onClose={() => setDeleteRecordTarget(null)}
        title={t('employee.deleteRecord')}
        schema={deleteRecordSchema}
        initialValues={deleteRecordTarget ? {} : null}
        onSave={handleDeleteRecordSave}
      />

      <GenericFormDrawer
        open={!!mfaResetTarget}
        onClose={() => setMfaResetTarget(null)}
        title={t('mfa.admin.resetTitle')}
        schema={mfaResetSchema}
        initialValues={mfaResetTarget ? {} : null}
        onSave={handleResetMfaSave}
        submitLabel={t('mfa.admin.resetSubmit')}
        submitDisabled={!mfaResetTarget?.status?.enabled}
      />

      <GenericFormDrawer
        open={!!unlockTarget}
        onClose={() => setUnlockTarget(null)}
        title={t('loginLock.unlockAction')}
        schema={unlockSchema}
        initialValues={unlockTarget ? {} : null}
        onSave={handleUnlockSave}
        submitLabel={t('loginLock.unlockAction')}
        submitDisabled={!unlockTarget?.status?.locked}
      />

      <ConfirmDialog
        isOpen={!!blockedTraces}
        type="info"
        hideCancel
        title={t('employee.cannotDeleteRecordTitle')}
        confirmText={t('common.close')}
        message={blockedTraces ? (
          <>
            {t('employee.cannotDeleteRecordMessage', { name: blockedTraces.name })}
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, textAlign: 'left' }}>
              {Object.entries(blockedTraces.traces).map(([key, count]) => (
                <li key={key}>{t(`employee.trace.${key}`, { count })}</li>
              ))}
            </ul>
          </>
        ) : null}
        onConfirm={() => setBlockedTraces(null)}
        onCancel={() => setBlockedTraces(null)}
      />

      {odActive && !isFormer && canWrite('EM') && (
        <InfoNote>{t('employee.exitViaOffboardingNote')}</InfoNote>
      )}

      <StatusToggle>
        <StatusButton $active={!isFormer} onClick={() => handleStatusChange('active')}>
          {t('employee.activeEmployees')}
        </StatusButton>
        <StatusButton $active={isFormer} onClick={() => handleStatusChange('former')}>
          {t('employee.formerEmployees')}
        </StatusButton>
      </StatusToggle>

      {useMemo(() => (
        <DataTable
          title={isFormer ? t('employee.formerEmployees') : t('employee.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={!isFormer && canWrite('EM')}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={!isFormer && !odActive && canWrite('EM') ? handleRowsTerminate : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={isFormer ? undefined : handleRowClick}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite, isFormer, odActive,
        handleNewButtonClick, handleRowsTerminate, filterList,
        handleFilterConfirm, handleDataStateChange, handleRowClick,
      ])}
    </Container>
  );
}
